var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Input, Select } from 'antd';
import { ArrowRightOutlined, MailOutlined, PhoneOutlined, PlusOutlined, SearchOutlined, SwapOutlined } from '@ant-design/icons';
import services from 'services';
import EmptyComponent from 'components/EmptyComponent';
import Loader from 'components/AntEnhancement/Loader';
import localeTranslator from 'library/Hocs/LocalTranslator';
import { Doctor as DoctorResource } from 'constants/resources';
import './style.css';
var Doctors = function (props) {
    var _a;
    var t = props.translate;
    var navigate = useNavigate();
    var doctorService = services.doctorService, specializationService = services.specializationService, permissionService = services.permissionService;
    var _b = useState(''), searchValue = _b[0], setSearchValue = _b[1];
    var _c = useState('all'), specialtyFilter = _c[0], setSpecialtyFilter = _c[1];
    var _d = useState('name-asc'), sortKey = _d[0], setSortKey = _d[1];
    var debounceTimeOut;
    var _e = doctorService.useDoctors({}), doctors = _e.data, doctorsLoading = _e.isLoading;
    var _f = specializationService.useSpecializations(), specializations = _f.data, specsLoading = _f.isLoading;
    var canShowDoctor = permissionService.hasPermissionToShow(DoctorResource);
    var canAddDoctor = permissionService.hasPermissionToAdd(DoctorResource);
    var isLoading = doctorsLoading || specsLoading;
    var specializationName = function (doctor) {
        var _a;
        return (((_a = specializationService
            .getSpecializationDataByID(specializations, doctor.getSpecializationId())) === null || _a === void 0 ? void 0 : _a.getName()) || '');
    };
    var filteredDoctors = useMemo(function () {
        var list = (doctors || []).filter(function (doctor) { return doctor.getUser(); });
        var query = searchValue.trim().toLowerCase();
        var specName = function (doctor) {
            var _a;
            return ((_a = specializationService
                .getSpecializationDataByID(specializations, doctor.getSpecializationId())) === null || _a === void 0 ? void 0 : _a.getName()) || '';
        };
        var matched = list.filter(function (doctor) {
            if (specialtyFilter !== 'all' && doctor.getSpecializationId() !== specialtyFilter) {
                return false;
            }
            if (!query)
                return true;
            var user = doctor.getUser();
            var haystack = [
                user.getName(),
                specName(doctor),
                user.getMobile(),
                user.getEmail()
            ]
                .join(' ')
                .toLowerCase();
            return haystack.includes(query);
        });
        matched.sort(function (a, b) {
            var nameA = a.getUser().getName().toLowerCase();
            var nameB = b.getUser().getName().toLowerCase();
            var cmp = nameA.localeCompare(nameB);
            return sortKey === 'name-asc' ? cmp : -cmp;
        });
        return matched;
    }, [doctors, searchValue, specialtyFilter, sortKey, specializations, specializationService]);
    var onSearchValueChange = function (event) {
        var value = event.target.value;
        clearTimeout(debounceTimeOut);
        debounceTimeOut = setTimeout(function () {
            setSearchValue(value);
        }, 300);
    };
    var goToNewDoctorForm = function () { return navigate('/doctors/new'); };
    var goToDoctorProfile = function (id) {
        if (!canShowDoctor)
            return;
        navigate("/doctors/".concat(id));
    };
    var toggleSort = function () {
        setSortKey(function (prev) { return (prev === 'name-asc' ? 'name-desc' : 'name-asc'); });
    };
    var specialtyOptions = useMemo(function () { return __spreadArray([
        { value: 'all', label: t('allSpecialties') }
    ], (specializations || []).map(function (spec) { return ({
        value: spec.getId(),
        label: spec.getName()
    }); }), true); }, [specializations, t]);
    return (_jsxs("div", { className: "doctors-screen", "data-onboarding": "page-list", children: [_jsxs("header", { className: "doctors-heading", children: [_jsx("div", { className: "doctors-heading__left", children: _jsxs("div", { className: "doctors-title-row", children: [_jsx("h1", { children: t('title') }), _jsx("span", { className: "doctors-count", children: t('countBadge', { count: (_a = doctors === null || doctors === void 0 ? void 0 : doctors.length) !== null && _a !== void 0 ? _a : 0 }) })] }) }), canAddDoctor ? (_jsx("div", { className: "doctors-heading__actions", children: _jsxs("button", { type: "button", className: "doctors-btn doctors-btn--primary", onClick: goToNewDoctorForm, children: [_jsx(PlusOutlined, {}), _jsx("span", { children: t('newDoctor') })] }) })) : null] }), _jsxs("div", { className: "doctors-toolbar", children: [_jsxs("div", { className: "doctors-toolbar__filters", children: [_jsxs("div", { className: "doctors-search", children: [_jsx(SearchOutlined, { className: "doctors-search__icon" }), _jsx(Input, { className: "doctors-search__input", placeholder: t('searchPlaceholder'), onChange: onSearchValueChange, allowClear: true, "aria-label": t('searchPlaceholder') })] }), _jsx(Select, { className: "doctors-select", value: specialtyFilter, onChange: function (value) { return setSpecialtyFilter(value); }, options: specialtyOptions, popupMatchSelectWidth: false })] }), _jsxs("div", { className: "doctors-toolbar__sort", children: [_jsx("span", { className: "doctors-sort-label", children: t('sortLabel') }), _jsxs("button", { type: "button", className: "doctors-sort-btn", onClick: toggleSort, "aria-label": t(sortKey === 'name-asc' ? 'sortNameAsc' : 'sortNameDesc'), children: [_jsx("span", { children: t(sortKey === 'name-asc' ? 'sortNameAsc' : 'sortNameDesc') }), _jsx(SwapOutlined, { rotate: 90 })] })] })] }), _jsx(Loader, { renderSpinner: isLoading, className: "doctors-body", children: filteredDoctors.length === 0 ? (_jsx("div", { className: "doctors-empty", children: _jsx(EmptyComponent, { description: t((doctors === null || doctors === void 0 ? void 0 : doctors.length) ? 'empty' : 'emptyText') }) })) : (_jsx("div", { className: "doctors-grid", children: filteredDoctors.map(function (doctor) {
                        var user = doctor.getUser();
                        var specialty = specializationName(doctor);
                        var displayName = "".concat(t('drPrefix'), " ").concat(user.getName())
                            .replace(/\s+/g, ' ')
                            .trim();
                        return (_jsxs("article", { className: [
                                'doctor-card',
                                canShowDoctor ? 'is-interactive' : ''
                            ]
                                .filter(Boolean)
                                .join(' '), onClick: function () { return goToDoctorProfile(doctor.getId()); }, "data-testid": "doctor-card-".concat(doctor.getId()), children: [_jsxs("div", { className: "doctor-card__main", children: [_jsx("h3", { className: "doctor-card__name", children: displayName }), specialty ? (_jsx("span", { className: "doctor-card__specialty", children: specialty })) : null, _jsxs("div", { className: "doctor-card__meta", children: [user.getMobile() ? (_jsxs("div", { className: "doctor-card__meta-row", children: [_jsx(PhoneOutlined, {}), _jsx("span", { className: "is-mono", children: user.getMobile() })] })) : null, user.getEmail() ? (_jsxs("div", { className: "doctor-card__meta-row is-muted", children: [_jsx(MailOutlined, {}), _jsx("span", { className: "is-truncate", children: user.getEmail() })] })) : null] })] }), canShowDoctor && (_jsx("div", { className: "doctor-card__footer", children: _jsxs("button", { type: "button", className: "doctor-card__view", onClick: function (event) {
                                            event.stopPropagation();
                                            goToDoctorProfile(doctor.getId());
                                        }, children: [_jsx("span", { children: t('view') }), _jsx(ArrowRightOutlined, {})] }) }))] }, doctor.getId()));
                    }) })) })] }));
};
export default localeTranslator(Doctors, 'doctors');
